import { type FieldMetadata, getTextareaProps } from '@conform-to/react';
import clsx from 'clsx';
import { type ElementType, type ReactNode } from 'react';
import { ErrorMessage, Field, Label } from '../catalyst-ui/fieldset';
import { Textarea } from '../catalyst-ui/textarea';
import { Explanation } from '../Explanation';

type Props = {
    textareaClassName?: string;
    field: FieldMetadata<any, Record<string, any>>;
    fieldClassName?: string;
    label?: ReactNode | string;
    labelClassName?: string;
    shortExplanation?: ReactNode | string;
    shortExplanationClassName?: string;
    longExplanation?: ReactNode | string;
    control?: ElementType;
} & Omit<Parameters<typeof Textarea>[0], 'name' | 'invalid' | 'className'>;

export function FormTextareaField({
    field,
    fieldClassName,
    textareaClassName,
    label,
    labelClassName,
    shortExplanation,
    shortExplanationClassName,
    longExplanation,
    control: Control = Textarea,
    ...inputProps
}: Props) {
    const { key, ...fieldInputProps } = getTextareaProps(field);
    return (
        <Field className={fieldClassName}>
            {label ? <Label className={clsx('block text-sm font-medium mb-1', labelClassName)}>{label}</Label> : null}
            <Control
                key={key}
                {...{ ...fieldInputProps, ...inputProps }}
                invalid={!field.valid}
                className={textareaClassName}
            />
            {shortExplanation || longExplanation
                ? (
                    <Explanation
                        short={shortExplanation}
                        long={longExplanation}
                        shortClassName={shortExplanationClassName}
                    />
                )
                : null}
            {field.valid ? null : field.errors?.map(x => <ErrorMessage key={x}>{x}</ErrorMessage>)}
        </Field>
    );
}
